// Native browser imports; no Turf namespace or CommonJS interop.
import { difference as clipDifference, intersection as clipIntersection } from 'polyclip-ts';
const featureCollection = features => ({ type: 'FeatureCollection', features });
const point = coordinates => ({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates } });
const feature = geometry => ({ type: 'Feature', properties: {}, geometry });
// Use the polygon clipping engine directly, avoiding Turf namespace exports.
function clippedFeature(coordinates) {
  if (!coordinates.length) return null;
  return feature(coordinates.length === 1
    ? { type: 'Polygon', coordinates: coordinates[0] }
    : { type: 'MultiPolygon', coordinates });
}
function difference(collection) {
  return clippedFeature(clipDifference(...collection.features.map(f => f.geometry.coordinates)));
}
function intersect(collection) {
  return clippedFeature(clipIntersection(...collection.features.map(f => f.geometry.coordinates)));
}
// Used only to detect non-zero overlap, not to report a distance or land area.
function area(f) {
  const polygons = f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates;
  return polygons.reduce((total, rings) => total + Math.abs(signedRingArea(rings[0])) -
    rings.slice(1).reduce((sum, ring) => sum + Math.abs(signedRingArea(ring)), 0), 0);
}
function booleanPointInPolygon(p, f) {
  const polygons = f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates;
  const coordinates = p.geometry.coordinates;
  return polygons.some(rings => {
    if (rings.some(ring => ring.slice(0, -1).some((a, i) => onSegment(coordinates, a, ring[i + 1])))) return true;
    return ringContains(coordinates, rings[0]) && !rings.slice(1).some(ring => ringContains(coordinates, ring));
  });
}
// Local planar topology checks avoid boolean-valid's browser export differences.
// Delivery areas use WGS84 longitude/latitude and do not cross the antimeridian.
function signedRingArea(ring) {
  const origin = ring[0];
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    sum += (ring[i][0] - origin[0]) * (ring[i + 1][1] - origin[1]) -
      (ring[i + 1][0] - origin[0]) * (ring[i][1] - origin[1]);
  }
  return sum / 2;
}
function cross(a, b, c) {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}
function onSegment(p, a, b) {
  return cross(a, b, p) === 0 && p[0] >= Math.min(a[0], b[0]) && p[0] <= Math.max(a[0], b[0]) && p[1] >= Math.min(a[1], b[1]) && p[1] <= Math.max(a[1], b[1]);
}
function segmentsMeet(a, b, c, d) {
  const abC = cross(a, b, c), abD = cross(a, b, d), cdA = cross(c, d, a), cdB = cross(c, d, b);
  return ((abC > 0 && abD < 0 || abC < 0 && abD > 0) && (cdA > 0 && cdB < 0 || cdA < 0 && cdB > 0)) ||
    onSegment(c, a, b) || onSegment(d, a, b) || onSegment(a, c, d) || onSegment(b, c, d);
}
function ringContains(point, ring) {
  let inside = false;
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i], b = ring[i + 1];
    if (onSegment(point, a, b)) return false;
    if ((a[1] > point[1]) !== (b[1] > point[1]) && point[0] < (b[0] - a[0]) * (point[1] - a[1]) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}
function ringsMeet(a, b) {
  for (let i = 0; i < a.length - 1; i++) {
    for (let j = 0; j < b.length - 1; j++) {
      if (segmentsMeet(a[i], a[i + 1], b[j], b[j + 1])) return true;
    }
  }
  return false;
}
function validateTopology(polygons) {
  for (const rings of polygons) {
    for (const ring of rings) {
      if (signedRingArea(ring) === 0) throw new Error('An area boundary must enclose some land.');
      const n = ring.length - 1;
      for (let i = 0; i < n; i++) {
        const a = ring[i], b = ring[i + 1];
        if (a[0] === b[0] && a[1] === b[1]) throw new Error('Remove repeated adjacent corners.');
        const previous = ring[(i + n - 1) % n];
        if (onSegment(previous, a, b) || onSegment(b, previous, a)) throw new Error('Area boundaries must not double back on themselves.');
        for (let j = i + 1; j < n; j++) {
          if (j === i + 1 || i === 0 && j === n - 1) continue;
          if (segmentsMeet(a, b, ring[j], ring[j + 1])) throw new Error('Area boundaries must not cross themselves.');
        }
      }
    }
    for (let h = 1; h < rings.length; h++) {
      if (!ringContains(rings[h][0], rings[0]) || ringsMeet(rings[0], rings[h])) throw new Error('A hole must lie inside its area boundary.');
      for (let k = 1; k < h; k++) {
        if (ringsMeet(rings[h], rings[k]) || ringContains(rings[h][0], rings[k]) || ringContains(rings[k][0], rings[h])) throw new Error('Area holes must not overlap.');
      }
    }
  }
  for (let i = 0; i < polygons.length; i++) {
    for (let j = i + 1; j < polygons.length; j++) {
      const intersection = intersect(featureCollection([
        feature({ type: 'Polygon', coordinates: polygons[i] }),
        feature({ type: 'Polygon', coordinates: polygons[j] })
      ]));
      if (intersection && area(intersection) > 0) throw new Error('Separate pieces of an area must not overlap.');
    }
  }
}

function validateAreas(areas) {
  if (!Array.isArray(areas)) throw new Error('Areas must be an array.');
  const ids = new Set(), names = new Set(), colors = new Set();
  return areas.map(a => {
    if (!a || typeof a.id !== 'string' || !a.id || a.id.length > 100 || ids.has(a.id)) throw new Error('Invalid or duplicate area ID.');
    if (typeof a.name !== 'string' || !a.name.trim() || a.name.length > 100 || names.has(a.name.trim().toLowerCase())) throw new Error('Each area needs a different name (up to 100 characters).');
    if (!/^#[0-9a-f]{6}$/i.test(a.color) || colors.has(a.color.toLowerCase())) throw new Error('Each area needs a different colour.');
    ids.add(a.id); names.add(a.name.trim().toLowerCase()); colors.add(a.color.toLowerCase());
    const g = a.geometry;
    if (!g || !['Polygon', 'MultiPolygon'].includes(g.type)) throw new Error('Only polygons are supported.');
    const polygons = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
    if (!Array.isArray(polygons) || !polygons.length) throw new Error('Empty polygon.');
    for (const rings of polygons) {
      if (!Array.isArray(rings) || !rings.length) throw new Error('Empty polygon.');
      for (const ring of rings) {
        if (!Array.isArray(ring) || ring.length < 4 || ring.some(p => !Array.isArray(p) || p.length !== 2 || !p.every(Number.isFinite) || Math.abs(p[0]) > 180 || Math.abs(p[1]) > 90)) throw new Error('Invalid polygon coordinates.');
        if (ring[0][0] !== ring[ring.length-1][0] || ring[0][1] !== ring[ring.length-1][1]) throw new Error('Polygon rings must be closed.');
      }
    }
    validateTopology(polygons);
    return { id: a.id, name: a.name.trim(), color: a.color.toLowerCase(), geometry: g };
  });
}
// The active polygon wins. Clipping retains holes and disconnected pieces.
function applyArea(areas, active) {
  validateAreas([...areas.filter(a => a.id !== active.id), active]);
  return [...areas.filter(a => a.id !== active.id).map(a => {
    const result = difference(featureCollection([feature(a.geometry), feature(active.geometry)]));
    return result ? { ...a, geometry: result.geometry } : null;
  }).filter(Boolean), active];
}
function normalizeAreas(areas) {
  return validateAreas(areas).reduce((out, a) => applyArea(out, a), []);
}
function postcodesInAreas(areas, points) {
  const out = Object.fromEntries(areas.map(a => [a.id, new Set()]));
  for (const p of points) {
    // Shared boundary belongs to the most recently adjusted area only.
    const a = [...areas].reverse().find(a => booleanPointInPolygon(point([p.lng, p.lat]), feature(a.geometry)));
    if (a) out[a.id].add(p.postcode);
  }
  return Object.fromEntries(Object.entries(out).map(([id, s]) => [id, [...s].sort()]));
}
export { validateAreas, applyArea, normalizeAreas, postcodesInAreas };
